import React, { useEffect } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { countryUrlMap } from '@adac-mp/shared';
import 'react-loading-skeleton/dist/skeleton.css';
import './styles/globals.css';
import { Home } from './pages/Home/home';
import { DkvCheckout, DkvConfiguration, OrderConfirmationDkvPage, Walkthrough } from './pages/dkv';
import { IntroCze, VignetteCheckoutCze, VignetteConfigurationCze } from './pages/vignette-cze';
import { IntroSvn, VignetteCheckoutSvn, VignetteConfigurationSvn } from './pages/vignette-svn';
import { IntroAut, VignetteCheckoutAut, VignetteConfigurationAut } from './pages/oesterreich';
import { IntroSchweiz, VignetteConfigurationSchweiz, VignetteCheckoutSchweiz } from './pages/schweiz';
import { OneCartConfiguration, VignetteCheckoutOneCart } from './pages/one-cart';
import {
	CancelOrderConfirmation,
	CancelOrderLocalization,
	CancelOrderRequest,
	EditPaymentMethod,
	EmptyCart,
	ErrorPage,
	LinkExpiredPage,
	NotFound,
	OrderConfirmation,
	OrderManagement,
	OrderOverview,
	ProcessingPage,
	ProductDonePage,
	SessionExpiredPage,
	TripsOverview,
} from './pages';

export const App: React.FC = () => {
	const location = useLocation();
	const isPreviewEnvironment = process.env.VERCEL_ENV !== 'production';

	useEffect(() => {
		// eslint-disable-next-line no-self-assign
		window.dataLayer = window.dataLayer;
		const isConfigPage = location.pathname.includes('config');
		if (!isConfigPage) window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
	}, [location.pathname]);

	return (
		<div className="bg-gray-warm -mb-3 sm:-mt-5 mt-7 lg:-mt-6 xl:mt-0">
			<Routes>
				{/* Home */}
				{isPreviewEnvironment && <Route path="" element={<Home />} />}
				{/* <Route path="/" element={<Navigate replace to="/camper-mautbox/onboarding" />} /> */}

				{/* DKV */}
				<Route path="camper-mautbox/onboarding" element={<Walkthrough />} />
				<Route path="camper-mautbox/configuration?step=1" element={<DkvConfiguration />} />
				<Route path="camper-mautbox/configuration" element={<DkvConfiguration />} />
				<Route path="camper-mautbox/checkout" element={<DkvCheckout />} />
				<Route path="camper-mautbox/order-confirmation" element={<OrderConfirmationDkvPage />} />

				{/* Vignettes */}
				{isPreviewEnvironment && (
					<>
						{/* id 6 stands for Czech Republic */}
						<Route path={`${countryUrlMap[6]}/onboarding`} element={<IntroCze />} />
						<Route path={`${countryUrlMap[6]}/configure`} element={<VignetteConfigurationCze />} />
						<Route path={`${countryUrlMap[6]}/checkout`} element={<VignetteCheckoutCze />} />
					</>
				)}
				{/* id 0 stands for One cart */}
				<Route path={`${countryUrlMap[0]}/configure`}>
					<Route path="" element={<OneCartConfiguration />} />
					<Route path=":lang" element={<OneCartConfiguration />} />
				</Route>
				<Route path={`${countryUrlMap[0]}/product-done`}>
					<Route path="" element={<ProductDonePage />} />
					<Route path=":lang" element={<ProductDonePage />} />
				</Route>
				<Route path={`${countryUrlMap[0]}/checkout`}>
					<Route path="" element={<VignetteCheckoutOneCart />} />
					<Route path=":lang" element={<VignetteCheckoutOneCart />} />
				</Route>

				{/* id 7 stands for Slovenia */}
				<Route path={`${countryUrlMap[7]}`}>
					<Route path="onboarding">
						<Route path="" element={<IntroSvn />} />
						<Route path=":lang" element={<IntroSvn />} />
					</Route>
					<Route path="configure">
						<Route path="" element={<VignetteConfigurationSvn />} />
						<Route path=":lang" element={<VignetteConfigurationSvn />} />
					</Route>
					<Route path="product-done">
						<Route path="" element={<ProductDonePage />} />
						<Route path=":lang" element={<ProductDonePage />} />
					</Route>
					<Route path="checkout">
						<Route path="" element={<VignetteCheckoutSvn />} />
						<Route path=":lang" element={<VignetteCheckoutSvn />} />
					</Route>
				</Route>

				{/* id 3 stands for Austria */}
				<Route path={`${countryUrlMap[3]}`}>
					<Route path="onboarding">
						<Route path="" element={<IntroAut />} />
						<Route path=":lang" element={<IntroAut />} />
					</Route>
					<Route path="configure">
						<Route path="" element={<VignetteConfigurationAut />} />
						<Route path=":lang" element={<VignetteConfigurationAut />} />
					</Route>
					<Route path="product-done">
						<Route path="" element={<ProductDonePage />} />
						<Route path=":lang" element={<ProductDonePage />} />
					</Route>
					<Route path="checkout">
						<Route path="" element={<VignetteCheckoutAut />} />
						<Route path=":lang" element={<VignetteCheckoutAut />} />
					</Route>
				</Route>

				{isPreviewEnvironment && (
					<Route path={`${countryUrlMap[14]}`}>
						{/* id 14 stands for Switzerland */}
						<Route path="onboarding">
							<Route path="" element={<IntroSchweiz />} />
							<Route path=":lang" element={<IntroSchweiz />} />
						</Route>
						<Route path="configure">
							<Route path="" element={<VignetteConfigurationSchweiz />} />
							<Route path=":lang" element={<VignetteConfigurationSchweiz />} />
						</Route>
						<Route path="product-done">
							<Route path="" element={<ProductDonePage />} />
							<Route path=":lang" element={<ProductDonePage />} />
						</Route>
						<Route path="checkout">
							<Route path="" element={<VignetteCheckoutSchweiz />} />
							<Route path=":lang" element={<VignetteCheckoutSchweiz />} />
						</Route>
					</Route>
				)}

				{/* Payments */}
				<Route path="processing">
					<Route path=":type">
						<Route path="" element={<ProcessingPage />} />
						<Route path=":lang" element={<ProcessingPage />} />
					</Route>
				</Route>

				{/* Orders */}
				<Route path="order-confirmation">
					<Route path="" element={<OrderConfirmation />} />
					<Route path=":lang" element={<OrderConfirmation />} />
				</Route>
				<Route path="cancel-order">
					<Route path="vignette">
						<Route path="" element={<CancelOrderLocalization />} />
						<Route path=":lang" element={<CancelOrderLocalization />} />
					</Route>
					{/* <Route path="dkv" element={<CancelOrder />} /> */}
				</Route>
				<Route path="cancel-order-request">
					<Route path="" element={<CancelOrderRequest />} />
					<Route path=":lang" element={<CancelOrderRequest />} />
				</Route>
				<Route path="order-management">
					<Route path="" element={<OrderManagement />} />
					<Route path=":lang" element={<OrderManagement />} />
				</Route>
				<Route path="order-overview">
					<Route path="" element={<OrderOverview />} />
					<Route path=":lang" element={<OrderOverview />} />
				</Route>
				<Route path="trips-overview">
					<Route path="" element={<TripsOverview />} />
					<Route path=":lang" element={<TripsOverview />} />
				</Route>
				<Route path="edit-payment-method">
					<Route path="" element={<EditPaymentMethod />} />
					<Route path=":lang" element={<EditPaymentMethod />} />
				</Route>
				<Route path="cancel-order-confirmation">
					<Route path="" element={<CancelOrderConfirmation />} />
					<Route path=":lang" element={<CancelOrderConfirmation />} />
				</Route>

				{/* Errors */}
				<Route path="error">
					<Route path="" element={<ErrorPage />} />
					<Route path=":lang" element={<ErrorPage />} />
				</Route>
				<Route path="empty-cart">
					<Route path="" element={<EmptyCart />} />
					<Route path=":lang" element={<EmptyCart />} />
				</Route>
				<Route path="session-expired">
					<Route path="" element={<SessionExpiredPage />} />
					<Route path=":lang" element={<SessionExpiredPage />} />
				</Route>
				<Route path="link-expired">
					<Route path="" element={<LinkExpiredPage />} />
					<Route path=":lang" element={<LinkExpiredPage />} />
				</Route>
				<Route path="*" element={<NotFound />} />
			</Routes>
			<div id="portal" />
		</div>
	);
};
